import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { InvoiceApi } from '@mesa-labs/mesa-api/dist/apis/invoice';
import { types as api, sdk } from '@mesa-labs/mesa-api';

import { prepareHeaders, useApi } from './api';

export const partnerRevSharesApi = createApi({
  reducerPath: 'partnerRevSharesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.invoiceUrl,
    prepareHeaders: async (headers) => await prepareHeaders(headers),
  }),
  tagTypes: ['PartnerRevShareConfigs', 'PartnerRevShareConfig'],
  endpoints: (build) => ({
    getAllPartnerRevShareConfigs: build.query<api.IPagedResults<api.PartnerRevShareConfigResponse>, { partnerId?: api.Partners; includeExpired?: boolean; page?: number; limit?: number }>({
      async queryFn({ partnerId, includeExpired, page, limit }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getAllPartnerRevShareConfigs({ partnerId, includeExpired, page, limit });
        return { data };
      },
      providesTags: ['PartnerRevShareConfigs'],
    }),

    getPartnerRevShareConfig: build.query<api.PartnerRevShareConfigResponse | undefined, { id: string }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getPartnerRevShareConfig(id);
        return { data };
      },
      providesTags: (result, error, { id }) => result ? [{ type: 'PartnerRevShareConfig' as const, id }] : ['PartnerRevShareConfig'],
    }),

    createPartnerRevShareConfig: build.mutation<api.PartnerRevShareConfigResponse, api.CreatePartnerRevShareConfigRequest>({
      async queryFn(body) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.createPartnerRevShareConfig(body);
        return { data };
      },
      invalidatesTags: ['PartnerRevShareConfigs'],
    }),

    updatePartnerRevShareConfig: build.mutation<api.PartnerRevShareConfigResponse | undefined, api.UpdatePartnerRevShareConfigRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.updatePartnerRevShareConfig(id, body);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'PartnerRevShareConfig' as const, id }, 'PartnerRevShareConfigs'],
    }),

    recalculatePartnerRevShareConfig: build.mutation<{ count: number }, { id: string }>({
      async queryFn({ id }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.recalculatePartnerRevShareConfig(id);
        return { data };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'PartnerRevShareConfig' as const, id }],
    }),

    getOperatorPartnerRevShareTotals: build.query<sdk.RevenueTotalsResponse, { partnerId: api.Partners; currency?: string }>({
      async queryFn({ partnerId, currency }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getOperatorPartnerRevShareTotals({ partnerId, currency: currency as api.CurrencyCode | undefined });
        return { data };
      },
    }),

    getOperatorPartnerRevShareStatistics: build.query<sdk.RevenueStatisticsDataPoint[], { partnerId?: api.Partners; periodization: api.TimeSeriesPeriodization; beginningAt?: string; endingAt?: string }>({
      async queryFn({ partnerId, periodization, beginningAt, endingAt }) {
        const invoiceApi = useApi((options) => new InvoiceApi(CONFIG.api.invoiceUrl, options));
        const data = await invoiceApi.getOperatorPartnerRevShareStatistics({ partnerId, periodization, beginningAt, endingAt });
        return { data };
      },
    }),
  }),
});

export const {
  useGetAllPartnerRevShareConfigsQuery,
  useGetPartnerRevShareConfigQuery,
  useCreatePartnerRevShareConfigMutation,
  useUpdatePartnerRevShareConfigMutation,
  useRecalculatePartnerRevShareConfigMutation,
  useGetOperatorPartnerRevShareTotalsQuery,
  useGetOperatorPartnerRevShareStatisticsQuery,
} = partnerRevSharesApi;
